import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import styled from 'styled-components';

import { DateRangePicker } from '../../cdl/DateRangePicker/DateRangePicker';
import { OverviewCustomerCompanySelect } from '../../common/form-elements/CompanySelect/OverviewCustomerCompanySelect';
import { OverviewSupplierCompanySelect } from '../../common/form-elements/CompanySelect/OverviewSupplierCompanySelect';
import { generateStatisticsDateRangePresets } from '../../common/helpers/generateStatisticsDateRangePresets';
import { useRole } from '../../common/hooks/useRole';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';

const ActionWrapper = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${theme.space[8]};

    @media (max-width: ${theme.breakpoints.lg}) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

export const StatisticsActions = () => {
    const presets = useMemo(generateStatisticsDateRangePresets, []);
    const role = useRole();

    const search = useSearch({ from: '/_app/statistics' });
    const navigate = useNavigate({ from: '/statistics' });

    const onDateChange = (dates: Date[]) => {
        navigate({
            search: (previousSearch) => ({
                ...previousSearch,
                from: dates[0].getTime(),
                to: dates[1].getTime(),
            }),
        });
    };

    return (
        <ActionWrapper>
            {role.isSuperAdmin() || (role.isCustomer() && !role.isOneCompanyUser()) ? (
                <OverviewCustomerCompanySelect
                    onChange={(customerIds) => {
                        navigate({
                            search: (previousSearch) => ({
                                ...previousSearch,
                                customerIds,
                            }),
                        });
                    }}
                    value={search.customerIds}
                />
            ) : null}

            {role.isSuperAdmin() || (role.isSupplier() && !role.isOneCompanyUser()) ? (
                <OverviewSupplierCompanySelect
                    onChange={(supplierIds) => {
                        navigate({
                            search: (previousSearch) => ({
                                ...previousSearch,
                                supplierIds,
                            }),
                        });
                    }}
                    value={search.supplierIds}
                />
            ) : null}

            <Box minWidth="320px">
                <DateRangePicker
                    dates={[new Date(search.from), new Date(search.to)]}
                    onDatesChange={(dates) => onDateChange(dates)}
                    presets={presets}
                />
            </Box>
        </ActionWrapper>
    );
};
